import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Send, Download, Receipt } from "lucide-react";

const statusColor = {
  delivered: "border-[var(--ra-success)] text-[var(--ra-success)]",
  paid: "border-[var(--ra-success)] text-[var(--ra-success)]",
  pending_payment: "border-[var(--ra-warning)] text-[var(--ra-warning)]",
  failed: "border-[var(--ra-error)] text-[var(--ra-error)]",
  cancelled: "border-[var(--ra-muted)] text-[var(--ra-muted)]",
};

const mpesaLabel = {
  requested: { text: "M-Pesa payout in progress", cls: "border-[var(--ra-warning)] text-[var(--ra-warning)]" },
  success: { text: "M-Pesa payout sent", cls: "border-[var(--ra-success)] text-[var(--ra-success)]" },
  failed: { text: "M-Pesa payout failed", cls: "border-[var(--ra-error)] text-[var(--ra-error)]" },
  timeout: { text: "M-Pesa payout timed out", cls: "border-[var(--ra-error)] text-[var(--ra-error)]" },
};

const History = () => {
  const [transfers, setTransfers] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/transfers").then(r => setTransfers(r.data)); }, []);

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="history-root">
      <div className="mb-8 fade-up flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-1.5">History</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Your transfers</h1>
          <p className="text-[var(--ra-muted)] mt-1">Every transfer, every receipt — all in one place.</p>
        </div>
        <Link to="/send"><Button className="btn-primary rounded-md h-11" data-testid="history-new-transfer"><Send size={16} className="mr-2" />New transfer</Button></Link>
      </div>

      {transfers.length === 0 ? (
        <div className="ra-card p-12 text-center fade-up fade-up-2" data-testid="history-empty">
          <Receipt size={40} className="mx-auto text-[var(--ra-muted)]" />
          <h3 className="font-heading text-lg font-semibold mt-3">No transfers yet</h3>
          <p className="text-sm text-[var(--ra-muted)] mt-1">Once you send money, receipts will appear here.</p>
        </div>
      ) : (
        <div className="ra-card p-0 overflow-hidden fade-up fade-up-2">
          <table className="w-full text-sm">
            <thead className="bg-[var(--ra-primary)]/5 text-[var(--ra-primary)] uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Reference</th>
                <th className="text-left px-5 py-3 font-semibold">Recipient</th>
                <th className="text-left px-5 py-3 font-semibold">Date</th>
                <th className="text-right px-5 py-3 font-semibold">Amount</th>
                <th className="text-center px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {transfers.map(t => (
                <tr key={t.id} className="border-t border-[var(--ra-border)]" data-testid={`history-row-${t.reference}`}>
                  <td className="px-5 py-3.5 font-mono text-xs">{t.reference}</td>
                  <td className="px-5 py-3.5">
                    <div className="font-medium">{t.recipient_snapshot.full_name}</div>
                    <div className="text-xs text-[var(--ra-muted)]">{t.recipient_snapshot.country_code}</div>
                  </td>
                  <td className="px-5 py-3.5 text-[var(--ra-muted)]">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="font-semibold">{formatMoney(t.source_amount, t.source_currency)}</div>
                    <div className="text-xs text-[var(--ra-muted)]">{formatMoney(t.destination_amount, t.destination_currency)}</div>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${statusColor[t.status] || ""}`}>{t.status.replace("_", " ")}</Badge>
                    {t.mpesa_status && mpesaLabel[t.mpesa_status] && (
                      <div className="mt-1.5">
                        <Badge variant="outline" className={`text-[9px] tracking-wide ${mpesaLabel[t.mpesa_status].cls}`} data-testid={`history-mpesa-${t.reference}`}>
                          {mpesaLabel[t.mpesa_status].text}
                        </Badge>
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <Button variant="ghost" size="sm" className="text-[var(--ra-primary)]" onClick={() => setSelected(t)} data-testid={`history-view-${t.reference}`}>View</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Receipt size={18} /> Receipt</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 mt-2" data-testid="receipt-modal">
              <div className="text-center">
                <div className="text-xs uppercase tracking-wider text-[var(--ra-muted)]">Reference</div>
                <div className="font-mono mt-1">{selected.reference}</div>
              </div>
              <div className="divide-y divide-[var(--ra-border)] border border-[var(--ra-border)] rounded-lg">
                <Row label="Recipient" value={selected.recipient_snapshot.full_name} />
                <Row label="Country" value={selected.recipient_snapshot.country_code} />
                <Row label="Method" value={selected.recipient_snapshot.delivery_method.replace("_", " ")} />
                <Row label="Account" value={selected.recipient_snapshot.account_identifier} />
                <Row label="You sent" value={formatMoney(selected.source_amount, selected.source_currency)} />
                <Row label="Fee" value={formatMoney(selected.fee, selected.source_currency)} />
                <Row label="Total paid" value={formatMoney(selected.total_charged, selected.source_currency)} bold />
                <Row label="Recipient received" value={formatMoney(selected.destination_amount, selected.destination_currency)} accent />
                <Row label="Rate" value={`1 ${selected.source_currency} = ${selected.rate} ${selected.destination_currency}`} />
                <Row label="Status" value={selected.status.replace("_", " ")} />
                {selected.mpesa_status && mpesaLabel[selected.mpesa_status] && (
                  <Row label="M-Pesa payout" value={mpesaLabel[selected.mpesa_status].text} />
                )}
                <Row label="Date" value={new Date(selected.created_at).toLocaleString()} />
              </div>
              <Button className="btn-primary w-full rounded-md" onClick={() => window.print()} data-testid="receipt-download-btn">
                <Download size={14} className="mr-2" /> Print / Save receipt
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ label, value, bold, accent }) => (
  <div className="px-4 py-2.5 flex justify-between text-sm">
    <span className="text-[var(--ra-muted)]">{label}</span>
    <span className={`${bold ? "font-bold" : "font-medium"} ${accent ? "text-[var(--ra-primary)]" : ""}`}>{value}</span>
  </div>
);

export default History;
